import { useEffect } from "react"

import { getPluginDetails } from "@fuse/api/pluginApi"
import { get } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"

import { eventDetailsState } from "@fuse/recoil/events"
import {
  pluginCacheState,
  pluginDetailsState,
  PluginDetailsState,
  pluginInstanceCacheState,
} from "@fuse/recoil/plugins"
import { UserRole } from "@fuse/recoil/pluginStudio/types"
import { userState } from "@fuse/recoil/signup"

/**
 * Custom hook for loading and managing plugin details
 *
 * @param pluginId - The ID of the plugin to load
 * @param eventId - The ID of the event (optional)
 */
const usePluginDetails = (pluginId: string, eventId?: string) => {
  const [detailsState, setDetailsState] = useRecoilState(pluginDetailsState)
  const [pluginCache, setPluginCache] = useRecoilState(pluginCacheState)
  const [instanceCache, setInstanceCache] = useRecoilState(
    pluginInstanceCacheState,
  )
  const eventState = useRecoilValue(eventDetailsState)
  const user = useRecoilValue(userState)

  // Get the effective event ID - either passed directly or from event state
  const effectiveEventId =
    eventId ||
    (eventState.eventDetails ? eventState.eventDetails.eventId : undefined)

  console.log("[usePluginDetails] Initial params:", {
    pluginId,
    eventId,
    effectiveEventId,
    eventDetailsAvailable: !!eventState.eventDetails,
    userLoggedIn: !!user,
    userId: user?.uid,
  })

  // Load plugin details from API
  const loadPluginDetails = async (skipCache = false) => {
    if (!pluginId) {
      console.warn("[usePluginDetails] No pluginId provided, skipping load")
      return
    }

    console.log("[usePluginDetails] Loading plugin details:", {
      pluginId,
      effectiveEventId,
      skipCache,
      cacheStatus: {
        pluginInCache: !!pluginCache[pluginId],
        instanceCacheExists: effectiveEventId
          ? !!instanceCache[effectiveEventId]
          : false,
        instanceInCache:
          effectiveEventId && instanceCache[effectiveEventId]
            ? !!instanceCache[effectiveEventId][pluginId]
            : false,
      },
    })

    // If plugin exists in cache and we're not skipping cache, use that
    if (!skipCache && pluginCache[pluginId]) {
      const cachedPlugin = pluginCache[pluginId]
      console.log("[usePluginDetails] Using cached plugin:", cachedPlugin)

      // Check if we also have an instance in cache
      let cachedInstance = null
      let isConnected = false

      if (effectiveEventId && instanceCache[effectiveEventId]) {
        cachedInstance = instanceCache[effectiveEventId][pluginId]
        isConnected = !!cachedInstance
        console.log("[usePluginDetails] Using cached instance:", {
          instanceId: cachedInstance?.id,
          isConnected,
        })
      } else {
        console.log(
          "[usePluginDetails] No cached instance found for this event/plugin",
        )
      }

      // Update state with cached data
      setDetailsState({
        plugin: cachedPlugin.plugin,
        instance: cachedInstance,
        isConnected,
        userRole: getEventUserRole(),
        isLoading: false,
        hasError: false,
      })

      // Fetch in background to update cache
      console.log("[usePluginDetails] Fetching in background to update cache")
      fetchPluginDetails(true)
    } else {
      console.log(
        "[usePluginDetails] No cache hit or skipCache=true, fetching fresh data",
      )
      // Set loading state
      setDetailsState({
        ...detailsState,
        isLoading: true,
        hasError: false,
      })

      // Fetch from API
      fetchPluginDetails(false)
    }
  }

  // Determine user role in the event
  const getEventUserRole = (): UserRole => {
    if (!effectiveEventId || !eventState.eventDetails || !user?.uid) {
      console.log("[usePluginDetails] Unable to determine user role:", {
        effectiveEventId: !!effectiveEventId,
        eventDetails: !!eventState.eventDetails,
        userLoggedIn: !!user?.uid,
      })
      return UserRole.GUEST
    }

    const event = eventState.eventDetails
    const userId = user.uid

    // Try different possible properties where creator ID might be stored
    const eventData = event.eventDocData || {}

    // Use safe property access with type checking
    const createdBy = get(eventData, "createdBy") || get(event, "createdBy")
    const initiatorId =
      get(eventData, "initiatorId") || get(event, "initiatorId")

    console.log("[usePluginDetails] Checking user role:", {
      userId,
      createdBy,
      initiatorId,
      eventDocDataExists: !!eventData,
    })

    if (
      // Check all possible creator ID properties using get from lodash
      createdBy === userId ||
      initiatorId === userId
    ) {
      console.log("[usePluginDetails] User is event host")
      return UserRole.HOST_EVENT_VIEW
    }

    // Could check for co-hosts, etc. here
    console.log("[usePluginDetails] User is event guest")
    return UserRole.GUEST
  }

  // Fetch plugin details from API
  const fetchPluginDetails = async (isBackground = false) => {
    try {
      console.log("[usePluginDetails] *** START API CALL ***", {
        pluginId,
        eventId: effectiveEventId,
        isBackground,
      })

      if (!isBackground) {
        setDetailsState({
          ...detailsState,
          isLoading: true,
          hasError: false,
        })
      }

      if (!effectiveEventId) {
        console.warn(
          "[usePluginDetails] No effective event ID available for API call",
        )
      }

      console.log("[usePluginDetails] About to call getPluginDetails API...")

      // Add timestamp for tracking API call timing
      const startTime = Date.now()

      const response = await getPluginDetails({
        eventId: effectiveEventId || "",
        pluginId,
      })

      const endTime = Date.now()
      console.log(
        `[usePluginDetails] API call completed in ${endTime - startTime}ms`,
      )

      // Check if response structure is as expected
      console.log("[usePluginDetails] Raw API response object:", response)

      console.log("[usePluginDetails] API response details:", {
        success: response.success,
        error: response.error,
        dataExists: !!response.data,
        responseType: typeof response,
        dataType: response.data ? typeof response.data : "undefined",
        pluginExists: response.data ? !!(response.data as any).plugin : false,
        instanceExists: response.data
          ? !!(response.data as any).instance
          : false,
      })

      if (response.success) {
        const { plugin, instance, isConnected, userRole } = response.data as any

        console.log("[usePluginDetails] Plugin details received:", {
          pluginId: plugin?.id,
          pluginName: plugin?.manifest?.name,
          instanceId: instance?.id,
          isConnected,
          userRole,
        })

        // Update plugin cache
        if (plugin) {
          setPluginCache((prev) => ({
            ...prev,
            [pluginId]: plugin,
          }))
        } else {
          console.warn(
            "[usePluginDetails] API returned success but no plugin data",
          )
        }

        // Update instance cache if available
        if (instance && effectiveEventId) {
          console.log("[usePluginDetails] Updating instance cache:", {
            instanceId: instance.id,
            eventId: effectiveEventId,
          })

          setInstanceCache((prev) => ({
            ...prev,
            [effectiveEventId]: {
              ...(prev[effectiveEventId] || {}),
              [pluginId]: instance,
            },
          }))
        }

        // Update state
        setDetailsState({
          plugin,
          instance,
          isConnected,
          userRole,
          isLoading: false,
          hasError: false,
        })
      } else {
        if (!isBackground) {
          console.error(
            "[usePluginDetails] API returned error:",
            response.error,
          )
          setDetailsState({
            ...detailsState,
            isLoading: false,
            hasError: true,
            errorMessage: response.error || "Failed to load plugin details",
          })
        }
      }
    } catch (error) {
      console.error(
        "[usePluginDetails] Exception fetching plugin details:",
        error,
      )

      if (!isBackground) {
        setDetailsState({
          ...detailsState,
          isLoading: false,
          hasError: true,
          errorMessage:
            error instanceof Error ? error.message : "Unknown error",
        })
      }
    }
  }

  // Load plugin details when pluginId or eventId changes
  useEffect(() => {
    console.log(
      "[usePluginDetails] *** DEPENDENCIES CHANGED *** Triggering effect",
      {
        time: new Date().toISOString(),
        pluginId,
        effectiveEventId,
        detailsStateExists: !!detailsState,
        pluginCacheSize: Object.keys(pluginCache).length,
        instanceCacheSize: Object.keys(instanceCache).length,
      },
    )

    if (pluginId) {
      console.log("[usePluginDetails] Calling loadPluginDetails()...")
      loadPluginDetails()
      console.log("[usePluginDetails] loadPluginDetails() called")
    } else {
      console.warn("[usePluginDetails] No pluginId available in useEffect")
    }

    // Debug return cleanup function
    return () => {
      console.log("[usePluginDetails] Effect cleanup running", {
        pluginId,
        effectiveEventId,
      })
    }
  }, [pluginId, effectiveEventId])

  return {
    ...detailsState,
    refresh: () => loadPluginDetails(true),
  }
}

export default usePluginDetails
